// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CompanySearch } from '@/components/company-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * If true, the manufacturer (current organization) is offered as an option.
   * @default true
   */
  showManufacturer?: boolean;

  /**
   * If true, the clear button is hidden and the selection can't be cleared.
   */
  disableClearable?: boolean;
}

/**
 * A controlled company search component that integrates with react-hook-form.
 */
export function FormCompanySearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // This component is always controlled, so an undefined value must be
  // coallesced to null.
  const value = field.value ?? null;

  return (
    <CompanySearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      onChange={field.onChange}
      showManufacturer={props.showManufacturer}
      disableClearable={props.disableClearable}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
    />
  );
}
